// Pattern: docs/design-patterns/frontend/state-management — server state in TanStack Query

import { useQuery } from '@tanstack/react-query';

import * as patternsApi from '~/services/api/design-patterns';

import type { PatternDetail, PatternExamples, PatternManifest } from '~/types';

import { useAuth } from './useAuth';

export const designPatternKeys = {
  all: ['design-patterns'] as const,
  manifest: () => [...designPatternKeys.all, 'manifest'] as const,
  detail: (id: string) => [...designPatternKeys.all, 'detail', id] as const,
  examples: (id: string, stack: string) =>
    [...designPatternKeys.all, 'examples', id, stack] as const,
};

const THIRTY_MINUTES = 30 * 60 * 1000;

export function usePatternDetail(patternId: string | undefined): {
  error: unknown;
  isLoading: boolean;
  pattern: PatternDetail | null;
} {
  const { loading, user } = useAuth();

  const {
    data: pattern,
    error,
    isLoading,
  } = useQuery({
    queryKey: designPatternKeys.detail(patternId ?? ''),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by `enabled: !!patternId`
    queryFn: () => patternsApi.getPattern(patternId!),
    enabled: !loading && !!user && !!patternId,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    pattern: pattern || null,
    isLoading,
    error,
  };
}

export function usePatternExamples(
  patternId: string,
  techStack: string | null,
): {
  error: unknown;
  examples: PatternExamples | null;
  isLoading: boolean;
} {
  const { loading, user } = useAuth();

  const {
    data: examples,
    error,
    isLoading,
  } = useQuery({
    queryKey: designPatternKeys.examples(patternId, techStack ?? ''),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by `enabled: !!techStack`
    queryFn: () => patternsApi.getPatternExamples(patternId, techStack!),
    enabled: !loading && !!user && !!techStack,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    examples: examples || null,
    isLoading,
    error,
  };
}

export function usePatternManifest(): {
  error: unknown;
  isLoading: boolean;
  manifest: PatternManifest | null;
} {
  const { loading, user } = useAuth();

  const {
    data: manifest,
    error,
    isLoading,
  } = useQuery({
    queryKey: designPatternKeys.manifest(),
    queryFn: patternsApi.getPatternManifest,
    enabled: !loading && !!user,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    manifest: manifest || null,
    isLoading,
    error,
  };
}
